<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectNoteItem from './ProjectNoteItem.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const draft = ref('');

const notes = computed(() =>
  store.getters['projectNotes/getNotesByProject'](Number(props.projectId))
);
const uiFlags = computed(() => store.getters['projectNotes/getUIFlags']);

const loadNotes = () =>
  store.dispatch('projectNotes/get', { projectId: props.projectId });

const addNote = async () => {
  const content = draft.value.trim();
  if (!content || uiFlags.value.isCreating) return;
  try {
    await store.dispatch('projectNotes/create', {
      projectId: props.projectId,
      content,
    });
    draft.value = '';
  } catch (error) {
    useAlert(t('PROJECTS.NOTES.ERROR'));
  }
};

const deleteNote = async noteId => {
  try {
    await store.dispatch('projectNotes/delete', {
      projectId: props.projectId,
      noteId,
    });
  } catch (error) {
    useAlert(t('PROJECTS.NOTES.ERROR'));
  }
};

watch(() => props.projectId, loadNotes);
onMounted(loadNotes);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Composer -->
    <div class="flex flex-col gap-2">
      <textarea
        v-model="draft"
        rows="2"
        :placeholder="t('PROJECTS.NOTES.PLACEHOLDER')"
        class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        @keydown.meta.enter="addNote"
        @keydown.ctrl.enter="addNote"
      />
      <Button
        :label="t('PROJECTS.NOTES.ADD')"
        icon="i-lucide-plus"
        size="sm"
        color="blue"
        class="self-end"
        :is-loading="uiFlags.isCreating"
        :disabled="!draft.trim()"
        @click="addNote"
      />
    </div>

    <!-- List -->
    <ProjectEmptyState
      v-if="!notes.length && !uiFlags.isFetching"
      icon="i-lucide-sticky-note"
      :title="t('PROJECTS.NOTES.EMPTY')"
    />
    <div v-else class="flex flex-col divide-y divide-n-weak/60">
      <ProjectNoteItem
        v-for="note in notes"
        :key="note.id"
        :note="note"
        @delete="deleteNote"
      />
    </div>
  </div>
</template>
